import { fabric } from 'fabric'
import Input from '../../../components/Input'
import TextAlignInput from '../../../components/TextAlignInput'
import FontCard from '../FontCard/FontCard'
import CircleColorPicker from '../../../components/CircleColorPicker'
import Card from '../../../components/Card'
import React, { useCallback, useMemo } from 'react'
import { StoredSpot } from '../../../types/Individualization'
import cx from 'classnames'
import styles from '../CustomizerPopup.module.css'
import { useTranslation } from 'react-i18next'
import { generateClipPath } from '../../../helpers/generateClipPath'
import PlusMinus from '../../../components/PlusMinus'
import { useCustomizer } from '../../../contexts/CustomizerContext'
import InputToolbar from "../common/InputToolbar/InputToolbar";
import { SettingType } from "../../../types/GlobalSetting";
import { usePluginContext } from '../../../contexts/PluginContext'

type TextElementInputProps = {
    spot: StoredSpot
    uid: string
    onDelete?: (unique_id: string) => void
    showHeading?: boolean
    autoFocus?: boolean
}

const TextElementInput: React.FC<TextElementInputProps> = ({
    spot,
    uid,
    onDelete,
    showHeading = true,
    autoFocus = false,
}) => {
    const {
        userInputs,
        setUserInputs,
        getFabricByUniqueId,
        getClipPath,
        fonts,
        activeTemplate,
        uuids,
    } = useCustomizer()

    const { getSettingValue } = usePluginContext()

    const filteredFonts = useMemo(() => {
        let tmpFonts = []
        if (spot.individual_font_family || spot.font_families.length === 0)
            return fonts
        else
            tmpFonts = fonts.filter((f) => {
                return spot.font_families.split(',').includes(f.id + '')
            })

        if (tmpFonts.length === 1 && tmpFonts[0].name && tmpFonts[0].path) {
            const fontFace = new FontFace(
                tmpFonts[0].name,
                `url(${tmpFonts[0].path})`
            )
            fontFace.load().then((f) => {
                document.fonts.add(f)
            })
        }
        return tmpFonts
    }, [fonts, spot])

    const updateSpot = useCallback(
        (
            storedSpot: StoredSpot,
            property: keyof fabric.Text,
            value: string,
            setActive: boolean = true
        ) => {
            const uuid =
                storedSpot.type !== 'cutting mask'
                    ? uuids.get(storedSpot.id)
                    : uid

            if (!uuid || !activeTemplate?.canvas) return

            const textElement = userInputs.get(uuid) as fabric.Text | null

            const clipPath = getClipPath(activeTemplate.canvas, storedSpot.id)

            if (!clipPath) return

            const selectable = !storedSpot.fixed_user_inputs

            if (textElement) {
                textElement.set(property, value)
                setUserInputs((prevData) => {
                    return new Map(prevData.set(uuid, textElement))
                })
                if (setActive)
                    activeTemplate.canvas.setActiveObject(textElement)
            } else {
                const text = new fabric.Text(value, {
                    hoverCursor: 'move',
                    top: clipPath.top,
                    left: clipPath.left,
                    angle: clipPath.angle,
                    originX: 'center',
                    originY: 'center',
                    fontSize: storedSpot.font_size,
                    fontFamily:
                        filteredFonts.length > 0
                            ? filteredFonts[0].name
                            : undefined,
                    fill: storedSpot.text_color ?? '#525252',
                    selectable: selectable,
                    evented: selectable,
                    centeredScaling: true,
                    centeredRotation: true,
                    data: {
                        unique_id: uuid,
                        spot_id: storedSpot.id,
                        template_id: storedSpot.template_id,
                        size: clipPath.data.size,
                        is_clip_path: false,
                    },
                    hasControls: selectable,
                    hasBorders: selectable,
                    borderColor: getSettingValue('border_color_spots_in_canvas', SettingType.COLOR) || '#FC0',
                    cornerColor: getSettingValue('border_color_spots_in_canvas', SettingType.COLOR) || '#FC0',
                    cornerStyle: 'circle',
                    transparentCorners: false,
                    cornerSize: 12,
                    hasRotatingPoint: selectable,
                    snapAngle: 1,
                })

                text.setControlsVisibility({
                    tl: false,
                    tr: false,
                    br: false,
                    bl: false,
                    ml: false,
                    mt: false,
                    mr: false,
                    mb: false,
                    mtr: selectable,
                })

                text.set('clipPath', generateClipPath(clipPath))

                setUserInputs((prev) => new Map(prev.set(uuid, text)))
                activeTemplate.canvas.add(text)
                if (setActive) activeTemplate.canvas.setActiveObject(text)
            }

            activeTemplate.canvas.requestRenderAll()
        },
        [
            uid,
            uuids,
            userInputs,
            activeTemplate,
            filteredFonts,
            getClipPath,
            setUserInputs,
            getSettingValue,
        ]
    )

    const changeTextProperty = useCallback(
        (property: keyof fabric.Text, value: string) => {
            updateSpot(spot, property, value)
            activeTemplate?.spots
                .filter((s) => s.sync_id === spot.id)
                .forEach((s) => {
                    updateSpot(s, property, value, false)
                })
        },
        [activeTemplate, updateSpot, spot]
    )

    const { t } = useTranslation(['customizer'])

    return (
        <Card
            key={spot.id}
            style={{boxShadow: !showHeading ? 'none' : undefined}}
        >
            <Card.Header>
                <div>{showHeading && spot.name}</div>
                <InputToolbar unique_id={uid} onDelete={onDelete} spot={spot}/>
            </Card.Header>

            { showHeading && <p dangerouslySetInnerHTML={{ __html: spot.hint ?? '' }} /> }
            <div>
                <Input
                    key={spot.id}
                    type={spot.multiline ? 'textarea' : 'text'}
                    placeholder={t('text_input')}
                    property={'text'}
                    value={
                        (getFabricByUniqueId(uid) as fabric.Text | null)
                            ?.text ?? ''
                    }
                    onChange={(_, value) => changeTextProperty('text', value)}
                    classes={'zr-my-3'}
                    maxLength={spot.max_character}
                    withCount={true}
                    autoFocus={autoFocus}
                />
                {(getFabricByUniqueId(uid) as fabric.Text | null) && (
                    <>
                        {spot.multiline && (
                            <div className={'zr-mb-3'}>
                                <span>{t('text_align')}</span>
                                <TextAlignInput
                                    property={'alignment'}
                                    onChange={(_, value) => {
                                        changeTextProperty('textAlign', value)
                                    }}
                                    value={
                                        (
                                            getFabricByUniqueId(
                                                uid
                                            ) as fabric.Text | null
                                        )?.textAlign ?? ''
                                    }
                                />
                            </div>
                        )}
                        {filteredFonts.length > 1 && (
                            <div>
                                <span>
                                    {t('font_family')}:{' '}
                                    {
                                        (
                                            getFabricByUniqueId(
                                                uid
                                            ) as fabric.Text | null
                                        )?.fontFamily
                                    }
                                </span>
                                <div
                                    className={cx(styles['text-font-wrapper'])}
                                >
                                    {filteredFonts
                                        .map((font) => (
                                            <FontCard
                                                chosen={
                                                    font.name ===
                                                    (
                                                        getFabricByUniqueId(
                                                            uid
                                                        ) as fabric.Text | null
                                                    )?.fontFamily
                                                }
                                                font={font}
                                                key={font.id}
                                                onClick={(font) => {
                                                    changeTextProperty(
                                                        'fontFamily',
                                                        font.name
                                                    )
                                                }}
                                            />
                                        ))}
                                </div>
                            </div>
                        )}
                        {spot.individual_text_color && (
                            <div
                                className={cx(
                                    styles['text-font-color-wrapper']
                                )}
                            >
                                <CircleColorPicker
                                    label={t('font_color_sub')}
                                    property={'fill'}
                                    value={
                                        (
                                            getFabricByUniqueId(
                                                uid
                                            ) as fabric.Text | null
                                        )?.fill?.toString() ?? '#000'
                                    }
                                    onChange={(_, value) => {
                                        changeTextProperty('fill', value)
                                    }}
                                />
                            </div>
                        )}
                        {spot.individual_font_size && (
                            <div
                                className={cx(
                                    styles['text-font-color-wrapper']
                                )}
                            >
                                <PlusMinus
                                    label={t('font_size_sub')}
                                    property={'font_size'}
                                    value={
                                        (
                                            getFabricByUniqueId(
                                                uid
                                            ) as fabric.Text | null
                                        )?.fontSize?.toString() ?? '20'
                                    }
                                    onChange={(_, value) => {
                                        changeTextProperty('fontSize', value)
                                    }}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
        </Card>
    )
}

export default TextElementInput
